/* eslint-disable no-unneeded-ternary */
import React from 'react';
import { Col, Row } from 'reactstrap';
import { useQuery } from '@apollo/client';
import { ORDER_LIST } from 'utils/CommonGraphQL';
import { VENDOR_ORDER_STATUS } from 'utils/constants';
import CardWithTitle from 'components/CardWithTitle';
import Loader from 'components/common/Loader';
import OrderCardDetails from './components/OrderCardDetails';
import OrderBiddingDetails from './components/OrderBiddingDetails';
import OrderProductDetails from './components/OrderProductDetails';
import PaymentDetails from './components/PaymentDetails';
import DeliveryStatus from './components/DeliverStatus';
import InvoiceList from '../../../InvoiceListDetailsDrawer/components/InvoiceList';
import OrderRequest from './components/OrderRequest';
import PolystoxApproval from './components/PolystoxApproval';

const OrderDetails = ({ orderId }) => {
  const { data, loading, error, refetch } = useQuery(ORDER_LIST, {
    variables: {
      filter: [{ key: 'id', value: orderId, type: 'INT', op: 'EQ' }],
    },
    fetchPolicy: 'network-only',
  });

  const currentRow = data?.orders?.orders?.[0];
  const shouldPoll =
    currentRow?.status === 'PENDING' &&
    !['ORDER_APPROVED', 'ORDER_REJECTED', 'ORDER_APPROVAL_EXPIRED'].includes(
      currentRow?.approvalStatus
    );

  // Refetch with polling when order is pending and not yet approved
  const { data: liveData } = useQuery(ORDER_LIST, {
    variables: {
      filter: [{ key: 'id', value: orderId, type: 'INT', op: 'EQ' }],
    },
    fetchPolicy: 'network-only',
    pollInterval: shouldPoll ? 3000 : 0, // Poll every 3 seconds only when pending
    skip: !shouldPoll,
  });

  const finalData = liveData || data;
  const finalCurrentRow = finalData?.orders?.orders?.[0];

  if (loading) return <Loader />;
  if (error) return `There is an error on this page : ${error.message} `;
  if (!finalData?.orders?.orders) return 'Orders not available';

  const {
    paymentTransitions,
    orderLogistics,
    gstAmount,
    tdsValue,
    tdsAmount,
    prepayment,
    convenienceAmount,
    total,
    subtotal,
    bid,
    gstValue,
    tcsValue,
    tcsAmount,
    paymentTerm,
    rateUnit,
    rate,
    quantity,
    insuranceAmount,
    isFreeTransitInsuranceIncluded,
    availInsurance,
    status,
    sellerCommissionAmount,
    buyerCommissionAmount,
  } = finalCurrentRow;

  return (
    <>
      <Row>
        <Col sm={9}>
          <OrderCardDetails currentRow={finalCurrentRow} />
          <CardWithTitle title="Delivery Status">
            {orderLogistics?.length ? (
              orderLogistics.map((item) => {
                return <DeliveryStatus logistic={item} key={item.id} />;
              })
            ) : (
              <span className="d-block mb-2">
                {status ? VENDOR_ORDER_STATUS[status] : 'Not Available'}
              </span>
            )}
          </CardWithTitle>
          <PaymentDetails
            paymentTransitions={paymentTransitions}
            gstAmount={gstAmount}
            gstValue={gstValue}
            tdsValue={tdsValue}
            tdsAmount={tdsAmount}
            tcsValue={tcsValue}
            tcsAmount={tcsAmount}
            prepayment={prepayment}
            convenienceAmount={convenienceAmount}
            total={total}
            subtotal={subtotal}
            paymentTerm={paymentTerm}
            rateUnit={rateUnit}
            rate={rate}
            quantity={quantity}
            insuranceAmount={insuranceAmount}
            isFreeTransitInsuranceIncluded={isFreeTransitInsuranceIncluded}
            availInsurance={availInsurance}
            sellerCommissionAmount={sellerCommissionAmount}
            buyerCommissionAmount={buyerCommissionAmount}
          />
          <OrderRequest order={finalCurrentRow} />
          <PolystoxApproval order={finalCurrentRow} onCompleted={refetch} />
          {bid && <OrderBiddingDetails bid={bid} />}
        </Col>
        <Col sm={3}>
          <OrderProductDetails currentRow={finalCurrentRow} />
        </Col>
      </Row>
      <CardWithTitle title="Invoice List" className="mt-3">
        <InvoiceList orderId={orderId} />
      </CardWithTitle>
    </>
  );
};

export default OrderDetails;
